import type { PublishingDocument } from "./document";

type Binding = Pick<PublishingDocument, "binding" | "pages">;

/** One pure binding derivation for sides and spreads. Composition, spread
 * guide validation and section alignment all read this — never a second
 * implementation of the alternation rule. Direction is already encoded in the
 * derived physical side (an RTL book starts on `firstSide: "right"`). */
export function derivePageSides(doc: Binding): ("left" | "right")[] {
  const first = doc.binding.firstSide;
  return doc.pages.map((_, index) =>
    !doc.binding.facing || index % 2 === 0
      ? first
      : first === "left"
        ? "right"
        : "left",
  );
}

export type DerivedSpread = {
  /** Derived display identity only (leading page); never persisted. */
  anchorPageId: string;
  leftPageId?: string;
  rightPageId?: string;
};

export function deriveSpreads(doc: Binding): DerivedSpread[] {
  const sides = derivePageSides(doc);
  const spreads: DerivedSpread[] = [];
  let current: DerivedSpread | undefined;
  doc.pages.forEach((page, index) => {
    const side = sides[index];
    const leading =
      !doc.binding.facing ||
      side === (doc.binding.direction === "ltr" ? "left" : "right");
    if (leading || !current) {
      current = { anchorPageId: page.id };
      spreads.push(current);
    }
    current[`${side}PageId`] = page.id;
  });
  return spreads;
}

/** The physical side a recto/verso start lands on: recto follows the spine in
 * reading direction (right for LTR, left for RTL). */
export function startSideToPhysical(
  startSide: "recto" | "verso",
  direction: "ltr" | "rtl",
): "left" | "right" {
  const recto = direction === "ltr" ? "right" : "left";
  return startSide === "recto" ? recto : recto === "left" ? "right" : "left";
}

export type SpreadAnchor = { leftPageId?: string; rightPageId?: string };

/** Returns the one derived spread that holds every known anchor in its stated
 * slot, or a named reason. Lone-left, lone-right and facing spreads all pass. */
export function matchSpreadAnchor(
  doc: Binding,
  anchor: SpreadAnchor,
): { spread: DerivedSpread } | { error: string } {
  if (!anchor.leftPageId && !anchor.rightPageId)
    return { error: "anchors no occurrence" };
  const known = new Set(doc.pages.map((p) => p.id));
  for (const id of [anchor.leftPageId, anchor.rightPageId])
    if (id && !known.has(id)) return { error: `anchors missing occurrence ${id}` };
  const spreads = deriveSpreads(doc);
  const probe = anchor.leftPageId ?? anchor.rightPageId;
  const spread = spreads.find(
    (s) => s.leftPageId === probe || s.rightPageId === probe,
  );
  if (!spread) return { error: "anchors an occurrence outside every spread" };
  if (anchor.leftPageId && spread.leftPageId !== anchor.leftPageId)
    return { error: "anchors occurrences that no longer share one spread slot" };
  if (anchor.rightPageId && spread.rightPageId !== anchor.rightPageId)
    return { error: "anchors occurrences that no longer share one spread slot" };
  return { spread };
}
